/**
 * Hong Kong Old Style ruleset.
 *
 * Pure and deterministic: no I/O, no clock, no randomness. Same inputs always
 * produce the same result, which is what makes it safe to run entirely on the
 * device and to unit-test exhaustively.
 */

import { validateTilePool } from '../tiles';
import type {
  DrawContext,
  HandInput,
  PatternHit,
  Payment,
  PlayerId,
  Ruleset,
  ScoreResult,
  WinContext,
} from '../types';
import { type Decomposition, decompose } from '../shared/decompose';
import {
  allTiles,
  detectHeavenlyEarthlyHand,
  detectLimitHands,
  detectPatterns,
  detectSevenPairs,
  detectSevenShiftedPairs,
  detectThirteenOrphans,
  isSevenPairsShape,
  sevenPairsBlockedByKong,
  totalFan,
} from './patterns';
import { settleDrawRound, settleWin } from './payout';
import { suggestPatterns } from './suggest';
import { DEFAULT_SETTINGS, activeFanCap, activeMinimumFan, activePayoutMethod, type HkosSettings } from './settings';

export * from './settings';
export { SUGGEST_MIN_TILES, suggestPatterns } from './suggest';

function validateHand(hand: HandInput, ctx: WinContext, _settings: HkosSettings): string[] {
  const problems = validateTilePool(allTiles(hand));

  const kongs = hand.melds.filter((m) => m.type === 'kong').length;
  // A kong upgrading one of Seven Pairs' pairs adds 2 tiles, not the 1 a
  // normal kong adds to a 4-sets-and-a-pair hand — so it would ALSO fail the
  // generic tile-count check below for a confusing reason ("wrong tile
  // count") rather than the real one. Reported on its own instead.
  if (sevenPairsBlockedByKong(hand)) {
    problems.push(
      'Seven Pairs cannot include a kong — a kong breaks the pairs structure. Keep it as a pair to score Seven Pairs.',
    );
  } else {
    const expected = 14 + kongs; // each kong adds one extra tile to the hand
    const actual = allTiles(hand).length;
    if (actual !== expected) {
      problems.push(
        `Hand has ${actual} tiles; expected ${expected} (14 + ${kongs} kong${kongs === 1 ? '' : 's'})`,
      );
    }
  }

  for (const m of hand.melds) {
    const size = m.type === 'kong' ? 4 : m.type === 'pair' ? 2 : 3;
    if (m.tiles.length !== size) {
      problems.push(`${m.type} meld has ${m.tiles.length} tiles, expected ${size}`);
    }
  }

  if (!hand.concealed.includes(hand.winningTile)) {
    problems.push(
      `Winning tile ${hand.winningTile} must be present in the concealed tiles`,
    );
  }

  // The replacement tile that wins the hand fills some OTHER slot — usually
  // the pair — never the kong itself: the kong was already a complete set
  // before that tile was drawn. Same principle as Self Triplets' "the
  // discard must complete the pair, not a triplet" rule. With only 4
  // physical copies of any tile, a winning tile that matches a kong's own
  // value would already need a 5th copy to exist in `concealed` too, so
  // this is also caught by the tile-pool check above — but it's worth its
  // own clear message rather than leaning on that generic one.
  const winningTileIsInAKong = hand.melds.some(
    (m) => m.type === 'kong' && m.tiles.includes(hand.winningTile),
  );

  // Unlike robbing-a-kong (which depends on another player's concealed
  // state — nothing here can check it), a kong-replacement claim is fully
  // checkable: it's about the winner's own melds and their own draw.
  if (ctx.kongReplacement) {
    if (kongs === 0) {
      problems.push('Kong replacement win claimed, but this hand has no kong.');
    }
    if (!ctx.selfDraw) {
      // The replacement tile is drawn from the dead wall by the winner
      // themselves — there is no discard-win variant of this pattern.
      problems.push('Kong replacement win must be a self-draw, not a discard win.');
    }
    if (winningTileIsInAKong) {
      problems.push(
        'Kong replacement win: the winning tile must complete the pair or another set — not be one of the kong\'s own tiles.',
      );
    }
  }

  if (ctx.winDoubleKong) {
    // The chain requires two kongs: the first's replacement tile completed
    // the second, whose own replacement tile is the winning tile.
    if (kongs < 2) {
      problems.push('Win Double Kong claimed, but this hand has fewer than two kongs.');
    }
    if (!ctx.selfDraw) {
      problems.push('Win Double Kong must be a self-draw, not a discard win.');
    }
    if (winningTileIsInAKong) {
      problems.push(
        'Win Double Kong: the winning tile must complete the pair or another set — not be one of either kong\'s own tiles.',
      );
    }
  }

  // Added 2026-09-22 alongside `lastDiscard` below — this check was
  // previously missing even though `kongReplacement`/`winDoubleKong` both
  // validate the self-draw/discard split for their own circumstance.
  if (ctx.lastWallTile && !ctx.selfDraw) {
    problems.push('Last wall tile must be a self-draw — it is won on the very last tile drawn from the wall.');
  }

  if (ctx.lastDiscard && ctx.selfDraw) {
    problems.push('Last discard must be a discard win — it is won by claiming the very last discard of the game.');
  }

  if (ctx.heavenlyHand) {
    if (!ctx.selfDraw) {
      problems.push('Heavenly Hand must be a self-draw — the dealer wins off their own starting hand.');
    }
    if (ctx.winner !== ctx.dealer) {
      problems.push('Heavenly Hand can only be claimed by the dealer.');
    }
  }

  if (ctx.earthlyHand) {
    if (ctx.selfDraw) {
      problems.push('Earthly Hand must be a discard win — a non-dealer claims the dealer’s first discard.');
    }
    if (ctx.winner === ctx.dealer) {
      problems.push('Earthly Hand can only be claimed by a non-dealer.');
    }
    if (ctx.discarder !== ctx.dealer) {
      problems.push('Earthly Hand requires the discard to have come from the dealer.');
    }
  }

  return problems;
}

/**
 * Shape-only check, no settings/context needed — Thirteen Orphans or any
 * valid 4-sets-and-a-pair reading both count. `detectThirteenOrphans` takes
 * a settings param only to stamp a fan value on the hit we're about to
 * discard, so a fixed default is fine here.
 */
function isCompleteHand(hand: HandInput): boolean {
  if (detectThirteenOrphans(hand, DEFAULT_SETTINGS)) return true;
  if (isSevenPairsShape(hand)) return true;
  return decompose(hand.concealed, hand.melds).length > 0;
}

function scoreHand(
  hand: HandInput,
  ctx: WinContext,
  settings: HkosSettings,
): ScoreResult {
  const notes: string[] = [];

  if (!ctx.selfDraw && !ctx.discarder) {
    notes.push('Discard win with no discarder recorded');
  }

  // Heavenly Hand / Earthly Hand are about how the deal happened, not the
  // tile shape — checked ahead of every shape-specific pattern, but still
  // gated on the tiles actually forming a valid winning shape (any shape:
  // Thirteen Orphans, Seven Pairs, or a standard decomposition all qualify).
  if (ctx.heavenlyHand || ctx.earthlyHand) {
    const hit = detectHeavenlyEarthlyHand(ctx, settings);
    if (hit && isCompleteHand(hand)) {
      return finalise([hit], settings, notes, true);
    }
  }

  // Thirteen Orphans and Seven Shifted Pairs are not 4-sets-and-a-pair
  // shapes, so they must be tested before the decomposer gets a chance to
  // reject the hand.
  const orphans = detectThirteenOrphans(hand, settings);
  if (orphans) {
    return finalise([orphans], settings, notes, true);
  }

  const shiftedPairs = detectSevenShiftedPairs(hand, settings);
  if (shiftedPairs) {
    return finalise([shiftedPairs], settings, notes, true);
  }

  // Seven Pairs (the non-shifted kind) is also not a 4-sets-and-a-pair
  // shape — detected in parallel with the standard decompositions below and
  // scored on the same footing (it isn't a forced limit, so it needs to
  // compete on total fan like everything else).
  const decompositions = decompose(hand.concealed, hand.melds);
  const sevenPairsHits = detectSevenPairs(hand, ctx, settings);

  if (decompositions.length === 0 && !sevenPairsHits) {
    return {
      fan: 0,
      effectiveFan: 0,
      patterns: [],
      isLimit: false,
      meetsMinimum: false,
      basepoints: 0,
      notes: [...notes, 'Not a winning shape: no valid 4 sets + pair reading'],
      rejection: { kind: 'incomplete' },
    };
  }

  // Standard-shape limit hands short-circuit the normal pattern scoring —
  // still gated on a real decomposition existing, exactly as before: a pure
  // Seven Pairs shape (decompositions.length === 0) must never be silently
  // reinterpreted as e.g. All Honours just because every tile happens to be
  // a wind/dragon.
  const limitHits = decompositions.length > 0 ? detectLimitHands(hand, ctx, settings) : [];
  if (limitHits.length > 0) {
    // The most valuable match wins (an All Kongs hand that is also Self
    // Triplets is worth 13, not 10); ties keep detection order.
    const best = limitHits.reduce((a, b) => (b.fan > a.fan ? b : a));
    if (limitHits.length > 1) {
      notes.push(
        `Multiple limit hands matched (${limitHits.map((h) => h.label).join(', ')}); scored as ${best.label}`,
      );
    }
    // Cap-value hits (`limit`) are forced limits; the fixed-value ones (10 faan)
    // are scored on their own value, which is still bounded by the cap.
    return finalise([best], settings, notes, best.limit === true);
  }

  // Score every reading, keep the most valuable one.
  let bestHits: PatternHit[] = sevenPairsHits ?? [];
  let bestFan = sevenPairsHits ? totalFan(sevenPairsHits) : -1;
  let bestDecomp: Decomposition | undefined;

  for (const d of decompositions) {
    const hits = detectPatterns(hand, d, ctx, settings);
    const fan = totalFan(hits);
    if (fan > bestFan) {
      bestFan = fan;
      bestHits = hits;
      bestDecomp = d;
    }
  }

  if (decompositions.length > 1 && bestDecomp) {
    notes.push(
      `Hand read ${decompositions.length} ways; scored the highest at ${bestFan} fan`,
    );
  }

  return finalise(bestHits, settings, notes, false);
}

function finalise(
  hits: PatternHit[],
  settings: HkosSettings,
  notes: string[],
  forcedLimit: boolean,
): ScoreResult {
  const fan = totalFan(hits);
  const capFan = activeFanCap(settings);
  const isLimit = forcedLimit || fan >= capFan;
  const effectiveFan = isLimit ? capFan : fan;

  if (!forcedLimit && fan > capFan) {
    notes.push(`${fan} fan capped at the ${capFan} fan limit`);
  }

  const minimumFan = activeMinimumFan(settings);
  const meetsMinimum = effectiveFan >= minimumFan;
  if (!meetsMinimum) {
    notes.push(
      `${effectiveFan} fan is below the ${minimumFan} fan minimum — not a valid win`,
    );
  }

  return {
    fan,
    effectiveFan,
    patterns: hits,
    isLimit,
    meetsMinimum,
    basepoints: meetsMinimum ? computeBasePoints(effectiveFan, settings) : 0,
    notes,
    ...(meetsMinimum ? {} : { rejection: { kind: 'below_minimum' as const, fan: effectiveFan, minimum: minimumFan } }),
  };
}

/**
 * How many points a hand of this (already-capped) fan is worth, per the
 * active mode. Serious mode has two substitutional curves — see
 * `activePayoutMethod` — never both at once: 'multiplier' mirrors Fun mode's
 * own linear curve (fan × multiplier), a simpler alternative to the
 * exponential default. `baseUnit` (the table stake) applies on top either
 * way, same as it always has.
 */
function computeBasePoints(effectiveFan: number, settings: HkosSettings): number {
  if (settings.mode === 'fun') {
    return Math.round(effectiveFan * settings.fun.multiplier);
  }
  const raw =
    activePayoutMethod(settings) === 'multiplier'
      ? effectiveFan * (settings.serious.multiplier ?? 1)
      : settings.serious.payoutBase ** effectiveFan;
  return Math.round(raw) * settings.serious.baseUnit;
}

export const hkos: Ruleset<HkosSettings> = {
  id: 'hkos',
  name: 'Hong Kong Old Style',
  defaultSettings: DEFAULT_SETTINGS,
  validate: validateHand,
  isCompleteHand,
  score: scoreHand,
  settle: (
    result: ScoreResult,
    ctx: WinContext,
    players: PlayerId[],
    settings: HkosSettings,
  ): Payment[] => settleWin(result, ctx, players, settings),
  settleDraw: (
    ctx: DrawContext,
    players: PlayerId[],
    settings: HkosSettings,
  ): Payment[] => settleDrawRound(ctx, players, settings),
  suggest: suggestPatterns,
};
